import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AISuggestionsPage() {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);

  const getHeaders = () => {
    const userId = localStorage.getItem('userId');
    return userId ? { 'X-User-Id': userId } : {};
  };

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ai/suggestions`, { 
        withCredentials: true,
        headers: getHeaders()
      });
      setSuggestions(response.data.suggestions || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'حدث خطأ في جلب الاقتراحات');
    } finally {
      setLoading(false);
    }
  };

  const analyzeCompatibility = async (targetUserId, targetName) => {
    setAnalyzing(targetUserId);
    setAnalysisResult(null);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/ai/analyze-compatibility`,
        { target_user_id: targetUserId },
        { withCredentials: true, headers: getHeaders() }
      );
      setAnalysisResult({ ...response.data.analysis, targetName: response.data.target_name });
    } catch (err) {
      setError('حدث خطأ في التحليل');
    } finally {
      setAnalyzing(null);
    }
  };

  const getCompatibilityColor = (score) => {
    if (score >= 80) return '#10B981';
    if (score >= 60) return '#34D399';
    if (score >= 40) return '#FBBF24';
    return '#F97316';
  };

  const getCompatibilityBg = (score) => {
    if (score >= 80) return 'linear-gradient(135deg, #10B981, #059669)';
    if (score >= 60) return 'linear-gradient(135deg, #34D399, #10B981)';
    if (score >= 40) return 'linear-gradient(135deg, #FBBF24, #F59E0B)';
    return 'linear-gradient(135deg, #F97316, #EA580C)';
  };

  const getRankStyle = (idx) => {
    const styles = [
      { bg: 'linear-gradient(135deg, #FFD700, #FFA000)', shadow: '0 4px 15px rgba(255, 215, 0, 0.4)' },
      { bg: 'linear-gradient(135deg, #E0E0E0, #9E9E9E)', shadow: '0 4px 15px rgba(158, 158, 158, 0.4)' },
      { bg: 'linear-gradient(135deg, #CD7F32, #A0522D)', shadow: '0 4px 15px rgba(205, 127, 50, 0.4)' },
    ];
    return styles[idx] || { bg: 'linear-gradient(135deg, #00897B, #00B4A2)', shadow: '0 4px 15px rgba(0, 137, 123, 0.3)' };
  };

  return (
    <div className="page-bg-mint" style={{ 
      minHeight: '100vh', 
      paddingBottom: '40px'
    }}>
      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '20px' }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px', paddingTop: '20px' }}>
          <div style={{ 
            fontSize: '50px', 
            marginBottom: '16px',
            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
          }}>🤖</div>
          <h1 style={{ 
            fontSize: '26px', 
            color: 'var(--primary)',
            fontFamily: "'Amiri', serif",
            marginBottom: '8px',
            fontWeight: '700'
          }}>
            اقتراحات التوافق الذكية
          </h1>
          <p style={{ color: 'var(--text-light)', fontSize: '14px' }}>
            تحليل بالذكاء الاصطناعي لإيجاد الشريك المناسب
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: 'var(--error)',
            padding: '16px',
            borderRadius: '12px',
            marginBottom: '20px',
            textAlign: 'center',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ 
              fontSize: '60px', 
              marginBottom: '20px', 
              animation: 'bounce 1s infinite'
            }}>🔍</div>
            <p style={{ color: 'var(--primary)', fontWeight: '600', fontSize: '16px' }}>جاري تحليل الملفات المتوافقة...</p>
            <p style={{ color: 'var(--text-light)', fontSize: '13px', marginTop: '8px' }}>قد يستغرق هذا بضع ثوانٍ</p>
          </div>
        )}

        {/* Suggestions List */}
        {!loading && suggestions.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {suggestions.map((suggestion, idx) => (
              <div 
                key={suggestion.user_id}
                style={{ 
                  background: 'var(--card-bg)',
                  borderRadius: '20px',
                  boxShadow: '0 4px 25px rgba(0, 0, 0, 0.08)',
                  overflow: 'hidden',
                  border: '1px solid rgba(0, 0, 0, 0.05)'
                }}
              >
                {/* Top Section with Gradient */}
                <div style={{
                  background: getCompatibilityBg(suggestion.compatibility),
                  padding: '20px',
                  position: 'relative'
                }}>
                  {/* Compatibility Score - على اليسار */}
                  <div style={{
                    position: 'absolute',
                    top: '16px',
                    left: '16px',
                    background: 'rgba(255,255,255,0.95)',
                    borderRadius: '16px',
                    padding: '8px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.15)'
                  }}>
                    <span style={{ 
                      fontSize: '20px', 
                      fontWeight: '800', 
                      color: getCompatibilityColor(suggestion.compatibility)
                    }}>
                      {suggestion.compatibility}%
                    </span>
                    <span style={{ fontSize: '11px', color: 'var(--text-light)' }}>توافق</span>
                  </div>

                  {/* Rank Badge - أعلى اليمين */}
                  <div style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    background: 'rgba(255,255,255,0.25)',
                    backdropFilter: 'blur(10px)',
                    color: '#fff',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '700',
                    fontSize: '14px'
                  }}>
                    #{idx + 1}
                  </div>

                  {/* Name and Basic Info - في الأسفل */}
                  <div style={{ color: '#fff', marginTop: '50px' }}>
                    <h3 style={{ 
                      fontSize: '24px', 
                      fontWeight: '700',
                      marginBottom: '10px',
                      textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      {suggestion.name}
                    </h3>
                    <div style={{ 
                      display: 'flex', 
                      gap: '16px', 
                      flexWrap: 'wrap', 
                      fontSize: '14px',
                      opacity: 0.95
                    }}>
                      {suggestion.age && <span>📅 {suggestion.age} سنة</span>}
                      {suggestion.city && <span>📍 {suggestion.city}</span>}
                      {suggestion.job && <span>💼 {suggestion.job}</span>}
                    </div>
                  </div>
                </div>

                {/* Content Section */}
                <div style={{ padding: '20px' }}>
                  
                  {/* Strengths */}
                  {suggestion.strengths && suggestion.strengths.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <h4 style={{ 
                        fontSize: '13px', 
                        color: 'var(--primary)', 
                        marginBottom: '10px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span style={{ fontSize: '16px' }}>✨</span>
                        نقاط التوافق:
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {suggestion.strengths.map((strength, i) => (
                          <div key={i} style={{
                            background: 'rgba(0, 217, 165, 0.1)',
                            border: '1px solid rgba(0, 217, 165, 0.3)',
                            color: 'var(--text-dark)',
                            padding: '10px 14px',
                            borderRadius: '10px',
                            fontSize: '13px',
                            lineHeight: '1.6'
                          }}>
                            {strength}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Discuss Point */}
                {suggestion.discuss && (
                  <div style={{ 
                    background: 'rgba(255, 193, 7, 0.1)',
                    border: '1px solid rgba(255, 193, 7, 0.3)',
                    padding: '12px 14px', 
                    borderRadius: '10px',
                    marginBottom: '16px'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '10px' 
                    }}>
                      <span style={{ fontSize: '18px' }}>💬</span>
                      <div>
                        <span style={{ 
                          color: 'var(--text-dark)', 
                          fontSize: '12px',
                          fontWeight: '600',
                          display: 'block',
                          marginBottom: '4px'
                        }}>
                          يُنصح بمناقشة:
                        </span>
                        <span style={{ color: 'var(--text-dark)', fontSize: '13px', lineHeight: '1.6' }}>
                          {suggestion.discuss}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => navigate(`/profile/${suggestion.user_id}`)}
                      style={{
                        flex: 1,
                        padding: '14px 20px',
                        background: '#10B981',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                      onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                      <span>👤</span>
                      عرض الملف
                    </button>
                    <button
                      onClick={() => analyzeCompatibility(suggestion.user_id, suggestion.name)}
                      disabled={analyzing === suggestion.user_id}
                      style={{
                        flex: 1,
                        padding: '14px 20px',
                        background: analyzing === suggestion.user_id ? '#D1D5DB' : '#8B5CF6',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: analyzing === suggestion.user_id ? 'wait' : 'pointer',
                        fontWeight: '600',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        boxShadow: analyzing === suggestion.user_id ? 'none' : '0 4px 15px rgba(139, 92, 246, 0.3)'
                      }}
                      onMouseOver={(e) => { if (analyzing !== suggestion.user_id) e.currentTarget.style.transform = 'translateY(-2px)' }}
                      onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                      <span>{analyzing === suggestion.user_id ? '⏳' : '🔍'}</span>
                      {analyzing === suggestion.user_id ? 'جاري التحليل...' : 'تحليل تفصيلي'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No Suggestions */}
        {!loading && suggestions.length === 0 && !error && (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            background: 'var(--card-bg)',
            borderRadius: '20px',
            boxShadow: '0 4px 25px rgba(0, 0, 0, 0.08)'
          }}>
            <div style={{ fontSize: '60px', marginBottom: '16px' }}>🔍</div>
            <p style={{ color: 'var(--text-dark)', fontWeight: '600', fontSize: '16px' }}>لا توجد اقتراحات حالياً</p>
            <p style={{ color: 'var(--text-light)', fontSize: '13px', marginTop: '8px' }}>سيتم إضافة اقتراحات جديدة قريباً</p>
          </div>
        )}

        {/* Analysis Modal */}
        {analysisResult && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            zIndex: 1000
          }} onClick={() => setAnalysisResult(null)}>
            <div 
              style={{ 
                maxWidth: '500px', 
                width: '100%', 
                padding: '28px',
                maxHeight: '85vh',
                overflow: 'auto',
                background: 'var(--card-bg)',
                borderRadius: '24px',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>📊</div>
                <h2 style={{ 
                  color: 'var(--primary)', 
                  fontSize: '20px',
                  fontWeight: '700'
                }}>
                  تحليل التوافق مع {analysisResult.targetName}
                </h2>
              </div>

              {/* Score */}
              {analysisResult.compatibility_score && (
                <div style={{ 
                  textAlign: 'center', 
                  marginBottom: '24px',
                  background: getCompatibilityBg(analysisResult.compatibility_score),
                  padding: '24px',
                  borderRadius: '16px'
                }}>
                  <div style={{
                    fontSize: '48px',
                    fontWeight: '800',
                    color: '#fff',
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    {analysisResult.compatibility_score}%
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>
                    {analysisResult.compatibility_level}
                  </div>
                </div>
              )}

              {/* Strengths */}
              {analysisResult.strengths?.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: 'var(--success)', marginBottom: '12px', fontSize: '15px', fontWeight: '600' }}>
                    ✅ نقاط التوافق
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {analysisResult.strengths.map((s, i) => (
                      <div key={i} style={{
                        background: 'rgba(0, 217, 165, 0.1)',
                        border: '1px solid rgba(0, 217, 165, 0.3)',
                        color: 'var(--text-dark)',
                        padding: '10px 14px',
                        borderRadius: '10px',
                        fontSize: '13px'
                      }}>{s}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Differences */}
              {analysisResult.differences?.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: 'var(--text-dark)', marginBottom: '12px', fontSize: '15px', fontWeight: '600' }}>
                    💬 نقاط للمناقشة
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {analysisResult.differences.map((d, i) => (
                      <div key={i} style={{
                        background: 'rgba(255, 193, 7, 0.1)',
                        border: '1px solid rgba(255, 193, 7, 0.3)',
                        color: 'var(--text-dark)',
                        padding: '10px 14px',
                        borderRadius: '10px',
                        fontSize: '13px'
                      }}>{d}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Advice */}
              {analysisResult.advice && (
                <div style={{ 
                  background: 'rgba(79, 172, 254, 0.1)',
                  border: '1px solid rgba(79, 172, 254, 0.3)',
                  padding: '16px', 
                  borderRadius: '12px',
                  marginBottom: '20px'
                }}>
                  <h4 style={{ color: 'var(--text-dark)', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>💡 نصيحة</h4>
                  <p style={{ color: 'var(--text-dark)', margin: 0, fontSize: '13px', lineHeight: '1.7' }}>{analysisResult.advice}</p>
                </div>
              )}

              {/* Suggested Questions */}
              {analysisResult.suggested_questions?.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: 'var(--purple)', marginBottom: '12px', fontSize: '15px', fontWeight: '600' }}>
                    ❓ أسئلة مقترحة للتعارف
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {analysisResult.suggested_questions.map((q, i) => (
                      <div key={i} style={{
                        background: 'rgba(192, 110, 237, 0.1)',
                        border: '1px solid rgba(192, 110, 237, 0.3)',
                        color: 'var(--text-dark)',
                        padding: '10px 14px',
                        borderRadius: '10px',
                        fontSize: '13px'
                      }}>{q}</div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={() => setAnalysisResult(null)}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: '#10B981',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '15px',
                  boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
                }}
              >
                إغلاق
              </button>
            </div>
          </div>
        )}

        {/* Back Button */}
        <div style={{ textAlign: 'center', marginTop: '32px' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '14px 32px',
              background: 'var(--card-bg)',
              color: 'var(--primary)',
              border: '2px solid var(--primary)',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)'
            }}
          >
            ← العودة للرئيسية
          </button>
        </div>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </div>
  );
}

export default AISuggestionsPage;
